/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Divider, Typography } from "@mui/material";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { useLocation, useNavigate } from "react-router-dom";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextareaInput } from "components/FormTextArea";
import { SimpleTextInput } from "components/FormTextInput";
import TranslationButtons from "components/TranslationButtons";
import configData from "config/config.json";
import useOpenClose from "hooks/useOpenClose";
import { useSnackbar } from "hooks/useSnackbar";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import {
  playerLabel,
  translationColumns,
  useTranslationModal,
} from "./translationHelpers";
import {
  getUsersUnderMaintenance,
  lookupUserByExternalId,
  toggleUserMaintenance,
} from "services/userMaintenanceService";

const DEFAULT_MESSAGE =
  "The game is unavailable right now. Please try again later.";

const UserMaintenanceEditor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { open, isOpen, close } = useOpenClose();

  const isCreate = location.pathname.endsWith("/create");

  const [loading, setLoading] = useState(!isCreate);
  const [record, setRecord] = useState<any>(null);
  const [searching, setSearching] = useState(false);
  const [matches, setMatches] = useState<any[]>([]);
  const [selectedRgsPlayerId, setSelectedRgsPlayerId] = useState("");
  const [details, setDetails] = useState({
    operatorId: "",
    externalPlayerId: "",
    message: DEFAULT_MESSAGE,
    messageTrans: { en: DEFAULT_MESSAGE } as Record<string, any>,
    reason: "",
  });

  const operatorOptions = sessionHelper.operatorOptions || [];
  const canUpdate = sessionHelper.hasRole(
    PermittedRoles.USER_MAINTENANCE.UPDATE_ROLES,
  );

  const translation = useTranslationModal({
    details,
    setDetails,
    open,
    close,
    showSnackbar,
  });

  const back = () =>
    navigate(configData.routes.userMaintenance.userMaintenance);

  const operatorName = (operatorId: string) =>
    operatorOptions.find((option: any) => option.id === operatorId)?.label ||
    operatorId;

  const rgsPlayerId = location.pathname.split("/").slice(-1)[0];

  const fetchRecord = async () => {
    try {
      setLoading(true);
      const response = await getUsersUnderMaintenance({
        pageNo: 1,
        pageSize: 1,
        rgsPlayerId,
      });
      const item = response.data?.items?.[0];

      if (!item) {
        showSnackbar("This user is no longer under maintenance", "error");
        back();
        return;
      }

      setRecord(item);
      setDetails({
        operatorId: item.operatorId,
        externalPlayerId: item.externalPlayerId || item.rgsPlayerId,
        message: item?.maintenance?.message || "",
        messageTrans: item?.maintenance?.messageTrans || {},
        reason: item?.maintenance?.reason || "",
      });
    } catch (error: any) {
      showSnackbar(error.message || "Failed to load user", "error");
      back();
    } finally {
      setLoading(false);
    }
  };

  const clearMatches = () => {
    setMatches([]);
    setSelectedRgsPlayerId("");
  };

  const onChange = (event: any) => {
    const { name, value } = event.target;
    const next: any = { ...details, [name]: value };
    if (name === "message")
      next.messageTrans = { ...details.messageTrans, en: value };
    setDetails(next);
    if (["operatorId", "externalPlayerId"].includes(name)) clearMatches();
  };

  const findPlayer = async () => {
    if (!details.operatorId || !details.externalPlayerId.trim()) {
      showSnackbar("Select an operator and enter an External User ID", "error");
      return;
    }

    try {
      setSearching(true);
      clearMatches();

      const response = await lookupUserByExternalId({
        operatorId: details.operatorId,
        externalPlayerId: details.externalPlayerId.trim(),
      });
      const players = response.data?.players || [];
      setMatches(players);

      if (players.length === 1) {
        setSelectedRgsPlayerId(players[0].rgsPlayerId);
        return;
      }

      showSnackbar(
        `${players.length} accounts share this External User ID, select the right one`,
        "warning",
      );
    } catch (error: any) {
      showSnackbar(error.message || "Failed to find user", "error");
    } finally {
      setSearching(false);
    }
  };

  const selectedPlayer = matches.find(
    (player) => player.rgsPlayerId === selectedRgsPlayerId,
  );

  const appliedDetail = [
    selectedPlayer?.maintenance?.appliedBy &&
      `by ${selectedPlayer.maintenance.appliedBy}`,
    selectedPlayer?.maintenance?.appliedAt &&
      `on ${new Date(selectedPlayer.maintenance.appliedAt).toLocaleString()}`,
  ]
    .filter(Boolean)
    .join(" ");

  const save = async () => {
    if (isCreate && !selectedRgsPlayerId) {
      showSnackbar("Find the user before saving", "error");
      return;
    }
    if (isCreate && selectedPlayer?.isUnderMaintenance) {
      showSnackbar("This user is already under maintenance", "error");
      return;
    }
    if (!details.message.trim()) {
      showSnackbar("Message shown to user is required", "error");
      return;
    }
    if (!details.reason.trim()) {
      showSnackbar("Reason is required", "error");
      return;
    }

    try {
      setLoading(true);
      const response = await toggleUserMaintenance({
        operatorId: details.operatorId,
        externalPlayerId: details.externalPlayerId.trim(),
        rgsPlayerId: isCreate ? selectedRgsPlayerId : record.rgsPlayerId,
        value: true,
        message: details.message,
        messageTrans: translation.cleanedTrans("messageTrans"),
        reason: details.reason,
        ...(isCreate ? {} : { isUpdate: true }),
      });
      showSnackbar(response.message, "success");
      back();
    } catch (error: any) {
      showSnackbar(error.message || "Failed to save maintenance mode", "error");
      setLoading(false);
    }
  };

  const lift = async () => {
    try {
      setLoading(true);
      const response = await toggleUserMaintenance({
        operatorId: details.operatorId,
        externalPlayerId: details.externalPlayerId.trim(),
        rgsPlayerId: record.rgsPlayerId,
        value: false,
      });
      showSnackbar(response.message, "success");
      back();
    } catch (error: any) {
      showSnackbar(error.message || "Failed to lift maintenance", "error");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isCreate) fetchRecord();
  }, []);

  return (
    <ContentShell
      title={isCreate ? "Add User Maintenance" : "User Under Maintenance"}
      loading={loading}
      actions={
        <Box style={{ display: "flex", gap: 8 }}>
          <Button
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={back}
          >
            {isCreate ? "Cancel" : "Back"}
          </Button>
          {canUpdate && (
            <>
              {!isCreate && (
                <Button variant="outlined" onClick={lift}>
                  Lift Maintenance
                </Button>
              )}
              <Button variant="contained" onClick={save}>
                Save
              </Button>
            </>
          )}
        </Box>
      }
    >
      {(isCreate || record) && (
        <Box style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Box
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box style={{ width: "240px" }}>
              <SimpleSelectInput
                readOnly={!isCreate}
                readonlyvalue={operatorName(details.operatorId)}
                label="Operator"
                name="operatorId"
                options={operatorOptions}
                value={details.operatorId}
                onChange={onChange}
              />
            </Box>
            <Box style={{ width: "240px" }}>
              <SimpleTextInput
                readOnly={!isCreate}
                readonlyvalue={details.externalPlayerId}
                label="External User ID"
                name="externalPlayerId"
                value={details.externalPlayerId}
                onChange={onChange}
              />
            </Box>
            {isCreate && (
              <Box style={{ marginTop: 24 }}>
                <CustomButton
                  startIcon={<PersonSearchIcon />}
                  onClick={findPlayer}
                  disabled={searching}
                >
                  Search User
                </CustomButton>
              </Box>
            )}
          </Box>

          {isCreate && matches.length > 1 && (
            <Box style={{ width: "440px" }}>
              <SimpleSelectInput
                label="Select the account"
                name="selectedRgsPlayerId"
                options={matches.map((player) => ({
                  id: player.rgsPlayerId,
                  label: playerLabel(player),
                }))}
                value={selectedRgsPlayerId}
                onChange={(e: any) => setSelectedRgsPlayerId(e?.target.value)}
              />
            </Box>
          )}

          {isCreate ? (
            selectedPlayer && (
              <>
                <Typography>
                  User: <strong>{playerLabel(selectedPlayer)}</strong>
                </Typography>
                {selectedPlayer.isUnderMaintenance && (
                  <Alert
                    severity="warning"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() =>
                          navigate(
                            configData.routes.userMaintenance.edit.replace(
                              ":id",
                              selectedPlayer.rgsPlayerId,
                            ),
                          )
                        }
                      >
                        View Record
                      </Button>
                    }
                  >
                    This user is already under maintenance
                    {appliedDetail ? ` (applied ${appliedDetail})` : ""}. Save
                    is disabled to avoid overwriting the existing record.
                  </Alert>
                )}
              </>
            )
          ) : (
            <Box style={{ width: "240px" }}>
              <SimpleTextInput
                readOnly
                readonlyvalue={record?.playerName}
                label="User Name"
                name="playerName"
                value={record?.playerName}
              />
            </Box>
          )}

          <Divider />

          <Box style={{ width: "440px" }}>
            <SimpleTextareaInput
              label="Message shown to user"
              name="message"
              value={details.message}
              onChange={onChange}
              buttons={
                <TranslationButtons
                  field="messageTrans"
                  onClick={translation.handleOpenModal}
                />
              }
            />
          </Box>

          <Box style={{ width: "440px" }}>
            <SimpleTextInput
              label="Reason (internal)"
              name="reason"
              value={details.reason}
              onChange={onChange}
            />
          </Box>
        </Box>
      )}

      <ViewLanguageModal
        isMultiline
        label="Select Language"
        isOpen={isOpen}
        close={close}
        onChange={translation.handleChange}
        handleOnSubmit={translation.handleAddTranslation}
        modalData={translation.modalData}
        actionHandlers={{
          handleLangEditClick: translation.handleLangEditClick,
          handleLangDeleteClick: translation.handleLangDeleteClick,
        }}
        tableColumns={translationColumns}
      />
    </ContentShell>
  );
};

export default UserMaintenanceEditor;
